import type { GeneralBarDatum } from "@jana-technology/platform-ui";
import type { ColumnDef } from "@tanstack/table-core";

import { handleNullishSummaryValue } from "components/tables/utils/formatters";
import {
  SystemOverviewMetricType,
  type RiskManagementMetricType,
} from "$/__generated__/graphql";
import { RiskByGroup } from "./types";
import {
  TOOLTIP_CATEGORY_HEADER,
  TOOLTIP_INDENT,
  TOOLTIP_SEMIBOLD,
} from "constants/styles";

import type {
  GenericFormatter,
  SummariesFormatters,
  SummariesFormattersGrouped,
  SummariesFormattersGroups,
  SummaryRecord,
  SummaryType,
  SummaryValue,
} from "./types";
import {
  formatRiskUnitValue,
  type RiskUnitFormatter,
} from "helpers/risk-units";

const getTooltipSubGroupDisplayLabel = (
  label: string | null | undefined,
  categoryHeader?: string,
): string => {
  const isCategoryTooltipSection =
    categoryHeader === "By Category" ||
    categoryHeader === "Consequence Type by Category";

  if (!isCategoryTooltipSection) {
    return label ?? "";
  }

  switch (label) {
    case "Downhole Repair":
      return "Repair - Downhole";
    case "Surface Repair":
      return "Repair - Surface";
    default:
      return label ?? "";
  }
};

type RiskSubGroup = {
  group: string;
  inTotal?: number | null;
  opTotal?: number | null;
  postMitigation?: number | null;
};

type RiskGroupWithSubGroups = {
  group: string;
  subGroups?: Array<RiskSubGroup> | null;
};

type TooltipSubGroup = {
  group?: string | null;
};

const getSortedTooltipSubGroups = <SubGroup extends TooltipSubGroup>(
  subGroups: Array<SubGroup> | null | undefined,
  categoryHeader: string | undefined,
): Array<{ subGroup: SubGroup; displayLabel: string }> => {
  return (subGroups ?? [])
    .map((subGroup) => ({
      subGroup,
      displayLabel: getTooltipSubGroupDisplayLabel(
        subGroup.group,
        categoryHeader,
      ),
    }))
    .sort((a, b) => a.displayLabel.localeCompare(b.displayLabel));
};

const forEachSortedTooltipSubGroup = (
  item: RiskGroupWithSubGroups,
  categoryHeader: string | undefined,
  callback: (subGroup: RiskSubGroup, displayLabel: string) => void,
): void => {
  const sortedSubGroups = getSortedTooltipSubGroups(
    item?.subGroups,
    categoryHeader,
  );

  for (const { subGroup, displayLabel } of sortedSubGroups) {
    callback(subGroup, displayLabel);
  }
};

export const makeBaseORIRDatum = (
  x: string,
): [GeneralBarDatum, GeneralBarDatum] => {
  return [
    {
      x,
      x1: "OR",
    },
    {
      x,
      x1: "IR",
    },
  ];
};

export const appendORIRData = <D extends RiskByGroup>(
  items: Array<D>,
  orDatum: GeneralBarDatum,
  orDataKeys: Array<string>,
  irDatum?: GeneralBarDatum,
  irDataKeys?: Array<string>,
): void => {
  // normalize various OR/IR keys (opTotal, operationalTotal, etc.)
  const orKey = "opTotal" in items[0] ? "opTotal" : "operationalTotal";
  const irKey = "inTotal" in items[0] ? "inTotal" : "interventionTotal";

  const handleDatum = (
    datum: GeneralBarDatum,
    item: D,
    key: "opTotal" | "operationalTotal" | "inTotal" | "interventionTotal",
    dataKeys?: Array<string>,
  ) => {
    const group = item.group!;

    datum[group] = item[key] ?? 0;

    if (Array.isArray(dataKeys) && !dataKeys.includes(group)) {
      dataKeys.push(group);
    }
  };

  for (const item of items) {
    if (item.group) {
      handleDatum(orDatum, item, orKey, orDataKeys);

      if (irDatum) {
        handleDatum(irDatum, item, irKey, irDataKeys);
      }
    }
  }
};

export const appendSystemOverviewTooltipExtras = (
  operationalTotal: number,
  interventionTotal: number,
  orDatum: GeneralBarDatum,
  irDatum: GeneralBarDatum,
  riskUnitFormatter: RiskUnitFormatter = formatRiskUnitValue,
): void => {
  orDatum.tooltipExtras ??= [];
  irDatum.tooltipExtras ??= [];

  orDatum.tooltipExtras = orDatum.tooltipExtras.concat([
    {
      label: "Risk Modality",
      value: "Operational Risk",
      labelClassName: TOOLTIP_SEMIBOLD,
    },
    {
      label: "Total Operational Risk",
      value: riskUnitFormatter(operationalTotal),
    },
    {
      label: "Total Combined Risk",
      value: riskUnitFormatter(operationalTotal + interventionTotal),
    },
  ]);

  irDatum.tooltipExtras = irDatum.tooltipExtras.concat([
    {
      label: "Risk Modality",
      value: "Intervention Risk",
      labelClassName: TOOLTIP_SEMIBOLD,
    },
    {
      label: "Total Intervention Risk",
      value: riskUnitFormatter(interventionTotal),
    },
    {
      label: "Total Combined Risk",
      value: riskUnitFormatter(operationalTotal + interventionTotal),
    },
  ]);
};

export const appendOverallRiskTooltipExtras = (
  operationalTotal: number,
  interventionTotal: number,
  datum: GeneralBarDatum,
  riskUnitFormatter: RiskUnitFormatter = formatRiskUnitValue,
): void => {
  datum.tooltipExtras ??= [];

  datum.tooltipExtras = datum.tooltipExtras.concat([
    {
      label: "Risk Modality",
      value: "Operational Risk",
      subGroup: "Operational Risk",
      labelClassName: TOOLTIP_SEMIBOLD,
    },
    {
      label: "Risk Modality",
      value: "Intervention Risk",
      subGroup: "Intervention Risk",
      labelClassName: TOOLTIP_SEMIBOLD,
    },
    {
      label: "Overall Operational Risk",
      value: riskUnitFormatter(operationalTotal),
    },
    {
      label: "Overall Intervention Risk",
      value: riskUnitFormatter(interventionTotal),
    },
    {
      label: "Overall Combined Risk",
      value: riskUnitFormatter(operationalTotal + interventionTotal),
    },
  ]);
};

export const appendPostMitigationRiskTooltipExtras = (
  riskMitigationName: string,
  baselineTotal: number,
  postMitigationTotal: number,
  datum: GeneralBarDatum,
  riskUnitFormatter: RiskUnitFormatter = formatRiskUnitValue,
): void => {
  datum.tooltipExtras ??= [];

  datum.tooltipExtras = datum.tooltipExtras.concat([
    {
      label: "Mitigation",
      value: "Baseline",
      subGroup: "Baseline",
      labelClassName: TOOLTIP_SEMIBOLD,
    },
    {
      label: "Mitigation",
      value: riskMitigationName,
      subGroup: "Post-Mitigation",
      labelClassName: TOOLTIP_SEMIBOLD,
    },
    {
      label: "Baseline Operational Risk",
      value: riskUnitFormatter(baselineTotal),
      subGroup: "Baseline",
    },
    {
      label: "Post-Mitigation Operational Risk",
      value: riskUnitFormatter(postMitigationTotal),
      subGroup: "Post-Mitigation",
    },
  ]);
};

export const appendSystemOverviewSubgroups = (
  items: Array<RiskGroupWithSubGroups>,
  orDatum: GeneralBarDatum,
  irDatum: GeneralBarDatum,
  categoryHeader?: string,
  riskUnitFormatter: RiskUnitFormatter = formatRiskUnitValue,
) => {
  const orTooltipExtras = (orDatum.tooltipExtras ??= []);
  const irTooltipExtras = (irDatum.tooltipExtras ??= []);

  if (categoryHeader) {
    orTooltipExtras.push({
      label: categoryHeader,
      value: "",
      labelClassName: TOOLTIP_CATEGORY_HEADER,
    });

    irTooltipExtras.push({
      label: categoryHeader,
      value: "",
      labelClassName: TOOLTIP_CATEGORY_HEADER,
    });
  }

  for (const item of items) {
    forEachSortedTooltipSubGroup(
      item,
      categoryHeader,
      (subGroup, displayLabel) => {
        orTooltipExtras.push({
          label: displayLabel,
          value: riskUnitFormatter(subGroup.opTotal ?? 0),
          subGroup: item.group,
          labelClassName: TOOLTIP_INDENT,
        });

        irTooltipExtras.push({
          label: displayLabel,
          value: riskUnitFormatter(subGroup.inTotal ?? 0),
          subGroup: item.group,
          labelClassName: TOOLTIP_INDENT,
        });
      },
    );
  }
};

export const appendRiskManagementSubgroups = (
  items: Array<RiskGroupWithSubGroups>,
  datum: GeneralBarDatum,
  categoryHeader?: string,
  riskUnitFormatter: RiskUnitFormatter = formatRiskUnitValue,
) => {
  const tooltipExtras = (datum.tooltipExtras ??= []);

  if (categoryHeader) {
    tooltipExtras.push({
      label: categoryHeader,
      value: "",
      labelClassName: TOOLTIP_CATEGORY_HEADER,
    });
  }

  for (const item of items) {
    forEachSortedTooltipSubGroup(
      item,
      categoryHeader,
      (subGroup, displayLabel) => {
        tooltipExtras.push(
          {
            label: displayLabel,
            value: riskUnitFormatter(subGroup.opTotal ?? 0),
            subGroup: "Operational Risk",
            labelClassName: TOOLTIP_INDENT,
          },
          {
            label: displayLabel,
            value: riskUnitFormatter(subGroup.inTotal ?? 0),
            subGroup: "Intervention Risk",
            labelClassName: TOOLTIP_INDENT,
          },
        );
      },
    );
  }
};

export const appendPostMitigationSubgroups = (
  items: Array<RiskGroupWithSubGroups>,
  datum: GeneralBarDatum,
  categoryHeader?: string,
  riskUnitFormatter: RiskUnitFormatter = formatRiskUnitValue,
) => {
  const tooltipExtras = (datum.tooltipExtras ??= []);

  if (categoryHeader) {
    tooltipExtras.push(
      {
        label: `Baseline ${categoryHeader}`,
        value: "",
        subGroup: "Baseline",
        labelClassName: TOOLTIP_CATEGORY_HEADER,
      },
      {
        label: `Post-Mitigation ${categoryHeader}`,
        value: "",
        subGroup: "Post-Mitigation",
        labelClassName: TOOLTIP_CATEGORY_HEADER,
      },
    );
  }

  for (const item of items) {
    forEachSortedTooltipSubGroup(
      item,
      categoryHeader,
      (subGroup, displayLabel) => {
        tooltipExtras.push(
          {
            label: displayLabel,
            value: riskUnitFormatter(subGroup.opTotal ?? 0),
            subGroup: "Baseline",
            labelClassName: TOOLTIP_INDENT,
          },
          {
            label: displayLabel,
            value: riskUnitFormatter(subGroup.postMitigation ?? 0),
            subGroup: "Post-Mitigation",
            labelClassName: TOOLTIP_INDENT,
          },
        );
      },
    );
  }
};

export const appendTopRiskWellsSubgroups = (
  topRiskWellsDatum: GeneralBarDatum,
  summaryInfo: Array<{
    group: string;
    operationalTotal?: number | null;
    interventionTotal?: number | null;
    items: Array<{
      group: string;
      operationalTotal?: number | null;
      interventionTotal?: number | null;
    }>;
  }> = [],
  totalType: "operationalTotal" | "interventionTotal" = "operationalTotal",
  categoryHeader?: string,
  riskUnitFormatter: RiskUnitFormatter = formatRiskUnitValue,
) => {
  topRiskWellsDatum.tooltipExtras = [];
  if (categoryHeader) {
    topRiskWellsDatum.tooltipExtras?.push({
      label: categoryHeader,
      value: "",
      labelClassName: TOOLTIP_CATEGORY_HEADER,
    });
  }
  for (const summary of summaryInfo) {
    const sortedSubGroups = getSortedTooltipSubGroups(
      summary.items,
      categoryHeader,
    );

    for (const { subGroup, displayLabel } of sortedSubGroups) {
      topRiskWellsDatum.tooltipExtras?.push({
        label: displayLabel,
        value: riskUnitFormatter(subGroup[totalType] ?? 0),
        subGroup: summary.group,
        labelClassName: TOOLTIP_INDENT,
      });
    }
  }
};

// for single bar stacks that use the same data as the grouped stacks
export const makeORTabData = (
  irTabData: Array<GeneralBarDatum>,
  orTabData: Array<GeneralBarDatum>,
) => {
  for (const tabDatum of irTabData) {
    if (tabDatum.x1 === "OR") {
      const { x1, ...rest } = tabDatum;

      if (rest.tooltipExtras) {
        rest.tooltipExtras = rest.tooltipExtras.filter(
          (tooltip) =>
            tooltip.label !== "Risk Modality" &&
            tooltip.label !== "Total Intervention Risk" &&
            tooltip.label !== "Total Combined Risk",
        );
      }

      orTabData.push(rest);
    }
  }
};

export const addFooterRecursively = <TData>(
  columns: Array<ColumnDef<TData>>,
  selectionSummaries: SummaryRecord | undefined,
  systemSummaries: SummaryRecord | undefined,
  columnRounding: SummariesFormattersGroups,
): Array<ColumnDef<TData>> =>
  columns.map((columnDef) => {
    // only accessor columns can contain data for summaries
    if ("accessorKey" in columnDef) {
      const summaryKey = columnDef.accessorKey as string;

      const deriveFooter = (
        summaries: SummaryRecord | undefined,
        summaryType: SummaryType,
      ) => {
        if (summaries === undefined) {
          return "Loading";
        } else if (
          typeof summaries === "object" &&
          Object.hasOwn(summaries, summaryKey)
        ) {
          return roundingHelper(
            summaries,
            summaryKey,
            columnRounding[summaryType] as Record<string, Intl.NumberFormat>,
          );
        }

        return "no-footer";
      };

      const selectionFooterValue = deriveFooter(
        selectionSummaries,
        "selection",
      );
      const systemFooterValue = deriveFooter(systemSummaries, "system");

      const updatedColumn = {
        ...columnDef,
        footer: [selectionFooterValue, systemFooterValue].includes("no-footer")
          ? undefined
          : () =>
              [selectionFooterValue, systemFooterValue].map(
                handleNullishSummaryValue,
              ),
      };

      return updatedColumn;
    }

    if ("columns" in columnDef && Array.isArray(columnDef.columns)) {
      columnDef.columns = addFooterRecursively(
        columnDef.columns,
        selectionSummaries,
        systemSummaries,
        columnRounding,
      );
    }

    return columnDef;
  });

const roundingHelper = (
  summaries: Record<string, SummaryValue>,
  summaryKey: string,
  columnRounding: Record<string, GenericFormatter | Intl.NumberFormat>,
) => {
  const summaryValue = summaries[summaryKey];
  const formatter = columnRounding[summaryKey];

  if (formatter) {
    // formatting all numbers could be a mistake; make sure to coerce formatters (or skipNumberFormat)
    if (typeof summaryValue === "number") {
      const intlFormatter = formatter as Intl.NumberFormat;

      return intlFormatter.format(summaryValue);
    } else {
      const genericFormatter = formatter as GenericFormatter;

      return genericFormatter(summaryValue);
    }
  }

  return summaryValue;
};

export const filterNullishOPINTotals = <T extends object>(
  data: Array<
    T & {
      opTotal?: number | null;
      inTotal?: number | null;
    }
  >,
): Array<
  T & {
    opTotal: number;
    inTotal: number;
  }
> =>
  data.filter(
    (datum): datum is T & { opTotal: number; inTotal: number } =>
      typeof datum.opTotal === "number" && typeof datum.inTotal === "number",
  );

export const filterNullishThreatTotals = <
  T extends object & {
    opTotal?: number | null;
    inTotal?: number | null;
    group: string;
  },
>(
  data: Array<T>,
): Array<
  Omit<T, "opTotal" | "inTotal"> & {
    opTotal?: number;
    inTotal?: number;
  }
> =>
  data.map(({ opTotal, inTotal, ...restThreat }) => {
    const isIntervention = restThreat.group.startsWith("Intervention");

    if (isIntervention && typeof inTotal === "number") {
      return { ...restThreat, inTotal };
    }

    if (!isIntervention && typeof opTotal === "number") {
      return { ...restThreat, opTotal };
    }

    return restThreat;
  });

export const convertOverallBarDataToSeries = <
  T extends object & {
    opTotal?: number;
    inTotal?: number;
    group: string;
  },
>(
  data: Array<T>,
): Array<GeneralBarDatum> => {
  return data.map((datum) => {
    const datumResult: GeneralBarDatum = {
      x: datum.group,
    };

    if (datum.opTotal !== undefined) {
      datumResult["Operational Risk"] = datum.opTotal;
      datumResult.customLabels = {
        ["Operational Risk"]: datum.group,
      };
    }

    if (datum.inTotal !== undefined) {
      datumResult["Intervention Risk"] = datum.inTotal;
      datumResult.customLabels = {
        ...(typeof datumResult.customLabels === "object" &&
        datumResult.customLabels !== null
          ? datumResult.customLabels
          : {}),
        ["Intervention Risk"]: datum.group,
      };
    }

    return datumResult;
  });
};

export const convertPostMitigationBarDataToSeries = <
  T extends object & {
    opTotal?: number | null;
    inTotal?: number | null;
    postMitigation?: number | null;
    group: string;
  },
>(
  data: Array<T>,
  postMitigationKey: "postMitigation" | "inTotal" = "postMitigation",
): Array<GeneralBarDatum> => {
  return data.map((datum) => {
    const datumResult: GeneralBarDatum = {
      x: datum.group,
    };

    if (datum.opTotal !== undefined) {
      datumResult.Baseline = datum.opTotal ?? 0;
      datumResult.customLabels = {
        Baseline: datum.group,
      };
    }

    if (datum[postMitigationKey] !== undefined) {
      datumResult["Post-Mitigation"] = datum[postMitigationKey] ?? 0;
      datumResult.customLabels = {
        ...(typeof datumResult.customLabels === "object" &&
        datumResult.customLabels !== null
          ? datumResult.customLabels
          : {}),
        "Post-Mitigation": datum.group,
      };
    }

    return datumResult;
  });
};

export const groupFooterFormatters = (
  selectionMetric: SystemOverviewMetricType | RiskManagementMetricType,
  systemMetric: SystemOverviewMetricType | RiskManagementMetricType,
  formatterMap: SummariesFormattersGrouped,
) => {
  const metricKeys = new Set(Object.values(SystemOverviewMetricType));
  const defaultFormatters = Object.fromEntries(
    Object.entries(formatterMap).filter(
      ([key]) => !metricKeys.has(key as SystemOverviewMetricType),
    ),
  ) as SummariesFormatters;

  const systemFormatters = formatterMap[systemMetric];
  const selectionFormatters = formatterMap[selectionMetric];

  return {
    system: { ...defaultFormatters, ...systemFormatters },
    selection: { ...defaultFormatters, ...selectionFormatters },
  };
};
